import { FC } from 'react';
import { Box, Button, Dialog, DialogContent, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { useLocalization } from 'Hooks';
import { DialogCloseComponent } from 'Components';

require('./StandardCardComparisonDialog.scss');

type ComparisonRow = {
  label: string;
  plusValue: string;
  standardValue: string;
};

type StandardCardComparisonDialogProps = {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  onContinuePlus: () => void;
  onContinueStandard: () => void;
};

const StandardCardComparisonDialog: FC<StandardCardComparisonDialogProps> = ({
  open,
  onClose,
  onCancel,
  onContinuePlus,
  onContinueStandard,
}) => {
  const localization = useLocalization();

  // Helper function to render values with color coding
  const renderValueWithColor = (value: string) => {
    const isVar = value === 'Var';
    const isYok = value === 'Yok';
    
    if (isVar) {
      return <span className="standardCardComparisonDialog__varValue">{value}</span>;
    }
    if (isYok) {
      return <span className="standardCardComparisonDialog__yokValue">{value}</span>;
    }
    return value;
  };

  const rows: ComparisonRow[] = [
    {
      label: localization.web_pc_istanbulcard_standard_compare_popup_row_1_label,
      plusValue: localization.web_pc_istanbulcard_standard_compare_popup_row_1_plus_value,
      standardValue: localization.web_pc_istanbulcard_standard_compare_popup_row_1_standard_value,
    },
    {
      label: localization.web_pc_istanbulcard_standard_compare_popup_row_2_label,
      plusValue: localization.web_pc_istanbulcard_standard_compare_popup_row_2_plus_value,
      standardValue: localization.web_pc_istanbulcard_standard_compare_popup_row_2_standard_value,
    },
    {
      label: localization.web_pc_istanbulcard_standard_compare_popup_row_3_label,
      plusValue: localization.web_pc_istanbulcard_standard_compare_popup_row_3_plus_value,
      standardValue: localization.web_pc_istanbulcard_standard_compare_popup_row_3_standard_value,
    },
    {
      label: localization.web_pc_istanbulcard_standard_compare_popup_row_4_label,
      plusValue: localization.web_pc_istanbulcard_standard_compare_popup_row_4_plus_value,
      standardValue: localization.web_pc_istanbulcard_standard_compare_popup_row_4_standard_value,
    },
    {
      label: localization.web_pc_istanbulcard_standard_compare_popup_row_5_label,
      plusValue: localization.web_pc_istanbulcard_standard_compare_popup_row_5_plus_value,
      standardValue: localization.web_pc_istanbulcard_standard_compare_popup_row_5_standard_value,
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="standardCardComparisonDialog">
      <DialogCloseComponent closeFunction={onClose} />
      <DialogContent className="standardCardComparisonDialog__content">
        <Box className="standardCardComparisonDialog__tableWrap">
          <Table className="standardCardComparisonDialog__table" size="small">
            <TableBody>
              <TableRow className="standardCardComparisonDialog__cardHeaderRow">
                <TableCell className="standardCardComparisonDialog__labelCell" />
                <TableCell className="standardCardComparisonDialog__valueCell standardCardComparisonDialog__cardHeaderCell">
                  <Box className="standardCardComparisonDialog__cardHeaderContent">
                    <img
                      className="standardCardComparisonDialog__cardImage"
                      src={localization.web_pc_istanbulcard_standard_compare_popup_plus_image}
                      alt={localization.web_pc_istanbulcard_standard_compare_popup_plus_title}
                    />
                    <Typography variant="h6" className="standardCardComparisonDialog__cardTitle">
                      {localization.web_pc_istanbulcard_standard_compare_popup_plus_title}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell className="standardCardComparisonDialog__valueCell standardCardComparisonDialog__cardHeaderCell">
                  <Box className="standardCardComparisonDialog__cardHeaderContent">
                    <img
                      className="standardCardComparisonDialog__cardImage"
                      src={localization.web_pc_istanbulcard_standard_compare_popup_standard_image}
                      alt={localization.web_pc_istanbulcard_standard_compare_popup_standard_title}
                    />
                    <Typography variant="h6" className="standardCardComparisonDialog__cardTitle">
                      {localization.web_pc_istanbulcard_standard_compare_popup_standard_title}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              {rows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell className="standardCardComparisonDialog__labelCell">{row.label}</TableCell>
                  <TableCell className="standardCardComparisonDialog__valueCell">{renderValueWithColor(row.plusValue)}</TableCell>
                  <TableCell className="standardCardComparisonDialog__valueCell">{renderValueWithColor(row.standardValue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box className="standardCardComparisonDialog__warning">
          {localization.web_pc_istanbulcard_standard_compare_popup_warning_text}
        </Box>

        <Box className="standardCardComparisonDialog__actions">
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={onContinuePlus}
            className="standardCardComparisonDialog__plusBtn"
          >
            {localization.web_pc_istanbulcard_standard_compare_popup_plus_button}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={onContinueStandard}
            className="standardCardComparisonDialog__standardBtn"
          >
            {localization.web_pc_istanbulcard_standard_compare_popup_standard_button}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={onCancel}
            className="standardCardComparisonDialog__cancelBtn"
          >
            {localization.web_pc_istanbulcard_standard_compare_popup_cancel_button}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StandardCardComparisonDialog;
